import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  ReactNode,
} from "react";
import {
  _Chef2024Container,
  _Chef2024Left,
  _Chef2024Right,
  _Chef2024RightWrapper,
  _LandingInnovationCarouselSection,
  _LandingInnovationProductList,
  _LandingInnovationRoot,
  _MosaiqueGrid,
} from "./LandingInnovation.styled";
import ScrollProgressBar from "./ScrollProgressBar/ScrollProgressBar";
import Typography from "../../../Foundation/Typography/Typography";
import { ImageResponsive } from "../../Image";
import { ImageFit } from "../../Image/interface/ImageData";
import HighlightedTypography from "../../HighlightedTypography/HighlightedTypography";
import { useHighlightedTypographyContext } from "../../HighlightedTypography";
import htmlParse from "html-react-parser";
import clsx from "clsx";
import FlexBox from "../../FlexBox/FlexBox";
import InnovationProduct, {
  IInnovationProductProps,
} from "./InnovationProduct/InnovationProduct";
import useBreakpoint from "../../Grid/hooks/useBreakpoint";

export type ILandingInnovationProduct<T extends any = any> = {
  id_product: string;
  imageId: string;
  title: string;
  subtitle?: string;
  description?: ReactNode;
  price?: string;
  priceSubtitle?: string;
  ctaLabel?: string;
  color?: string;
  displayFromMention?: string;
  displayPrice?: boolean;
} & T;

interface ILandingInnovationProps<T extends any = any> {
  title: string;
  highlightedTitle?: string;
  description?: string;
  coverImageId?: string;
  /** Bloc cover ambiance (desktop/mobile). Si défini, remplace ImageResponsive legacy. */
  coverAmbianceSlot?: React.ReactNode;
  products?: ILandingInnovationProduct<T>[];
  variant?: "ambiance-visual" | "product-list" | "chef-2024" | "mosaique";
  onClickProduct?: (product: ILandingInnovationProduct<T>) => void;
  showLessLabel: string;
  showMoreLabel: string;
  renderProduct?: (data: IInnovationProductProps, index: number) => ReactNode;
}
const LandingInnovation = <T extends any = any>(
  props: ILandingInnovationProps<T>
) => {
  const {
    title,
    highlightedTitle,
    description,
    coverImageId,
    coverAmbianceSlot,
    products,
    variant = "ambiance-visual",
    onClickProduct,
    showLessLabel,
    showMoreLabel,
    renderProduct,
  } = props;
  const coverRatio = 3 / 1;
  const isProductList = variant === "product-list";
  const isChef2024 = variant === "chef-2024";
  const isMosaique = variant === "mosaique";
  const isAmbianceVisual = variant === "ambiance-visual";
  const { mobile } = useBreakpoint();
  const { highlightedColor } = useHighlightedTypographyContext();

  const productList = useMemo(() => {
    return (
      products?.slice(
        0,
        isProductList ? 6 : isChef2024 ? 7 : isMosaique ? 4 : 3
      ) || []
    );
  }, [products, isProductList, isChef2024, isMosaique]);

  const productListScrollRef = useRef<HTMLDivElement>(null);
  const [carouselScrollProgress, setCarouselScrollProgress] = useState(0);
  const [carouselProgressHidden, setCarouselProgressHidden] = useState(true);

  const updateCarouselScrollProgress = useCallback(() => {
    const element = productListScrollRef.current;
    const itemCount = productList.length;
    if (!element || itemCount <= 1) {
      setCarouselScrollProgress(itemCount === 1 ? 1 : 0);
      setCarouselProgressHidden(true);
      return;
    }
    const maxScroll = element.scrollWidth - element.clientWidth;
    if (maxScroll <= 0) {
      setCarouselScrollProgress(1 / itemCount);
      setCarouselProgressHidden(true);
      return;
    }
    setCarouselProgressHidden(false);
    const scrollRatio = Math.min(1, Math.max(0, element.scrollLeft / maxScroll));
    setCarouselScrollProgress((1 + scrollRatio * (itemCount - 1)) / itemCount);
  }, [productList.length]);

  useLayoutEffect(() => {
    if (!isAmbianceVisual || !mobile) {
      setCarouselProgressHidden(true);
      return;
    }
    updateCarouselScrollProgress();
    const element = productListScrollRef.current;
    if (!element) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      updateCarouselScrollProgress();
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [
    isAmbianceVisual,
    mobile,
    productList,
    updateCarouselScrollProgress,
  ]);

  const itemRenderer = (
    product: ILandingInnovationProduct<T>,
    index: number,
    selectiveView?: IInnovationProductProps["selectiveView"]
  ) => {
    const _data: IInnovationProductProps = {
      className: "innovation-product-item",
      product: product,
      variant: isChef2024
        ? "chef-2024"
        : isMosaique
        ? "mosaique"
        : isProductList
        ? "default"
        : "text-on-desktop",
      onClick: onClickProduct,
      showLessLabel: showLessLabel,
      showMoreLabel: showMoreLabel,
      hideCta: isMosaique,
      selectiveView,
    };
    const showBreak =
      isChef2024 || isMosaique ? false : index > 1 && index % 3 === 0;
    return (
      <React.Fragment key={product.id_product}>
        {showBreak && (
          <div
            className="break"
            style={{
              flexBasis: "100%",
              height: 0,
            }}
          ></div>
        )}
        {renderProduct ? (
          renderProduct(_data, index)
        ) : (
          <InnovationProduct key={product.id_product} {..._data} />
        )}
      </React.Fragment>
    );
  };

  return (
    <_LandingInnovationRoot
      className="landing-innovations"
      $linkColor={highlightedColor}
    >
      <div className={clsx("landing-innovations__container", variant)}>
        {/* header */}
        <FlexBox
          direction="column"
          align="center"
          className="landing-innovations__header"
        >
          <HighlightedTypography
            variant="h4"
            align="center"
            highlightedText={highlightedTitle}
            className="landing-innovations__title"
          >
            {title}
          </HighlightedTypography>
          <Typography
            variant="body"
            align="center"
            className="landing-innovations__description"
          >
            {htmlParse(description + "")}
          </Typography>
        </FlexBox>

        {/* cover */}
        {variant === "ambiance-visual" && (
          <div className="landing-innovations__cover">
            {coverAmbianceSlot !== undefined && coverAmbianceSlot !== null ? (
              coverAmbianceSlot
            ) : (
              <ImageResponsive
                alt=""
                desktop={{
                  image: {
                    id: coverImageId,
                    fit: ImageFit.COVER,
                    quality: 80,
                    aspectRatio: coverRatio,
                  },
                  imgStyle: {
                    overflow: "hidden",
                    aspectRatio: coverRatio,
                    width: "100%",
                  },
                }}
                mobile={{
                  image: {
                    id: coverImageId,
                    fit: ImageFit.COVER,
                    quality: 100,
                    aspectRatio: coverRatio,
                    outputPixelDensities: [1, 2],
                  },
                  imgStyle: {
                    overflow: "hidden",
                    aspectRatio: coverRatio,
                    width: "100%",
                  },
                }}
              />
            )}
          </div>
        )}

        {/* Product list */}
        {variant === "chef-2024" ? (
          !mobile ? (
            <_Chef2024Container>
              <_Chef2024Left>
                {productList[0] && itemRenderer(productList[0], 0, "image")}
                {productList[3] && itemRenderer(productList[3], 3)}
                {productList[6] && itemRenderer(productList[6], 6)}
              </_Chef2024Left>
              <_Chef2024Right>
                <_Chef2024RightWrapper>
                  <div className="top-content">
                    {productList[0] && itemRenderer(productList[0], 0, "text")}
                  </div>
                  {productList[1] && itemRenderer(productList[1], 1)}
                  {productList[2] && itemRenderer(productList[2], 2)}
                  {productList[4] && itemRenderer(productList[4], 4)}
                  {productList[5] && itemRenderer(productList[5], 5)}
                </_Chef2024RightWrapper>
              </_Chef2024Right>
            </_Chef2024Container>
          ) : (
            <FlexBox direction="column" gap={24} style={{ width: "100%" }}>
              {productList?.map((product, index) => {
                return itemRenderer(product, index);
              })}
            </FlexBox>
          )
        ) : variant === "mosaique" ? (
          <_MosaiqueGrid>
            {productList?.map((product, index) => {
              return itemRenderer(product, index);
            })}
          </_MosaiqueGrid>
        ) : (
          <_LandingInnovationCarouselSection>
            <_LandingInnovationProductList
              ref={productListScrollRef}
              $isList={isProductList}
              $itemFullWidth={
                productList.length < 3 && isAmbianceVisual
              }
              onScroll={
                isAmbianceVisual && mobile
                  ? updateCarouselScrollProgress
                  : undefined
              }
            >
              <FlexBox
                justify="center"
                style={{
                  maxWidth: 1118,
                  width: "100%",
                  scrollSnapAlign: "start",
                  margin: "0 auto",
                }}
              >
                <FlexBox
                  className={"innovation-product-list-wrapper"}
                  justify={"space-between"}
                >
                  {productList?.map((product, index) => {
                    return itemRenderer(product, index);
                  })}
                </FlexBox>
              </FlexBox>
            </_LandingInnovationProductList>
            {isAmbianceVisual && mobile && productList.length > 0 && (
              <ScrollProgressBar
                className="landing-innovations__carousel-progress"
                progress={carouselScrollProgress}
                hidden={carouselProgressHidden}
              />
            )}
          </_LandingInnovationCarouselSection>
        )}
      </div>
    </_LandingInnovationRoot>
  );
};

export type { InnovationProductVisualRenderContext } from "./InnovationProduct/InnovationProduct";

export default LandingInnovation;
